import type {
  BuildState,
  BuildDerived,
  ComponentContribution,
  FOCZone,
  Shaft,
} from '@/types';
import { SHAFTS } from '@/data/shafts';
import { INSERTS } from '@/data/inserts';
import { NOCKS } from '@/data/nocks';
import { VANES } from '@/data/vanes';
import { POINTS } from '@/data/points';
import { estimateVelocity, ballisticCoefficient, centerOfPressure } from './ballistics';

// ============================================================
// Constants
// ============================================================

/**
 * Imperial constants for arrow physics.
 *
 * Sources:
 *  - KE divisor 450,240 = 2 × 32.174 ft/s² × 7000 gr/lb. This yields ft·lb when
 *    mass is in grains and velocity in fps.   [Easton Technical Bulletin]
 *  - Momentum divisor 225,400 = 7000 gr/lb × 32.174 ft/s² gives slug·ft/s when
 *    mass is in grains and velocity in fps.   [Engineering Toolbox / Easton]
 */
const KE_DIVISOR = 450_240;
const MOMENTUM_DIVISOR = 225_400;

/**
 * Component position constants (inches from nock throat).
 *
 * The ATA/Easton FOC standard measures arrow length L from the nock throat to
 * the *end of the shaft* (not the point tip). Components are assigned moment
 * positions at their centroid:
 *
 *  - Nock body: ~0.30–0.45" from throat depending on nock model.
 *  - Vane cluster: 3-fletch standard puts vane base ~0.75" from throat,
 *    vane length ~2–3", centroid ~1.5–2.5" depending on fletching length.
 *  - Wrap: typically begins 0.4" from throat, centroid at base + length/2.
 *  - Shaft centroid: L/2 (uniform mass distribution).
 *  - Insert: lives in the last 0.5–1.5" of the shaft. Centroid ~0.75" from tip.
 *  - Point: attached at L, with its own centroid in front of the shaft tip
 *    depending on category — field ~0.55" past tip, fixed BH ~0.85", mechanical
 *    BH ~0.65" (lighter front blades, ferrule weight close to tip).
 *
 * Sources for centroid positions: Iron Will Outfitters "FOC explained" white
 * paper; ArrowTrade Magazine "Arrow building 101", Issue 12 vol 28; field
 * measurements averaged across common SKUs.
 */
const POS_NOCK = 0.35;
/**
 * Typical distance from nock throat to back of shaft (the carbon).
 * Most modern nocks (Easton X, Nock-2-It, Firenock A) push out about 0.5"
 * past the shaft. Combined with the user-set vaneBackPosition (back-of-shaft
 * to back-of-fletch), this gives the full nock-throat-to-back-of-vane offset.
 */
const NOCK_THROAT_TO_SHAFT_BACK = 0.5;
/** Default if state.vaneBackPosition is undefined (legacy migration). */
const DEFAULT_VANE_BACK_POSITION = 1.0;
const WRAP_BASE_OFFSET = 0.4;
const INSERT_OFFSET_FROM_TIP = 0.75;

/**
 * Firenock CTI rear placement — distance from the rear cut of the shaft (back
 * of the carbon) to the start of the CTI tube.
 *
 * Why 2": when a lighted nock or Firenock A/X is installed, the battery + LED
 * circuit assembly occupies the first ~2" of the rear of the shaft. The CTI
 * tube must start forward of that volume. Per Firenock build guidance (and
 * confirmed by Sal on rebuilds), 2.0" is the average starting offset.
 *
 * This matters for FOC, balance point, and weight distribution — a 30-gr
 * CTI tube placed 2" forward of where the old math assumed (right against the
 * nock) shifts the CG forward by a non-trivial amount on shorter arrows.
 */
const CTI_REAR_OFFSET_FROM_SHAFT_BACK = 2.0;

/**
 * Computes the user's effective vane base offset from nock throat.
 * Adds the typical nock protrusion + the user-set back-of-shaft → back-of-fletch.
 * Example: nock sticks out 0.5", user fletches 1" from back of shaft →
 * vane back sits 1.5" from nock throat.
 */
function getVaneBaseOffset(state: BuildState): number {
  const vbp = state.vaneBackPosition ?? DEFAULT_VANE_BACK_POSITION;
  return NOCK_THROAT_TO_SHAFT_BACK + vbp;
}

const POINT_CENTROID_OFFSET: Record<string, number> = {
  field: 0.55,
  'fixed-broadhead': 0.85,
  'mechanical-broadhead': 0.65,
};

/** Wrap weight ~1.0 gr/in for 4-mil mylar. Heavier vinyl can be 1.5 gr/in. */
const WRAP_GR_PER_INCH = 1.0;

/** Default vane length (inches) used for cluster centroid when a vane has no length. */
const DEFAULT_VANE_LENGTH = 2.0;

function parseVaneLength(s: string): number {
  const m = s.match(/[\d.]+/);
  return m ? parseFloat(m[0]) : DEFAULT_VANE_LENGTH;
}

// ============================================================
// Catalog accessors
// ============================================================

export function getShaft(state: BuildState): Shaft {
  return SHAFTS[state.shaftIdx] ?? SHAFTS[0];
}

export function getSpineGPI(state: BuildState): number {
  const shaft = getShaft(state);
  // Custom shaft override — user sets GPI directly on the Builder slider.
  if (shaft.isCustom) {
    return state.customShaftGpi ?? 8.5;  // Sensible default for unset custom
  }
  return shaft.spines[state.spineKey] ?? Object.values(shaft.spines)[0];
}

export function getVaneWeight(state: BuildState): number {
  return VANES[state.vaneIdx]?.weight ?? 0;
}

export function getInsertWeight(state: BuildState): number {
  const insert = INSERTS[state.insertIdx];
  if (!insert) return 0;
  // Custom HIT insert override — user sets weight directly on the Builder slider.
  if (insert.isCustom) {
    return state.customInsertWeight ?? insert.weight;
  }
  return insert.weight;
}

/**
 * Impact collar weight — separate from HIT insert weight because users
 * pick collars independently in the real world. Returns 0 when no collar
 * is selected (collarIdx = -1 or undefined). Honors custom-collar override.
 */
export function getCollarWeight(state: BuildState): number {
  const idx = state.collarIdx ?? -1;
  if (idx < 0) return 0;
  const collar = INSERTS[idx];
  if (!collar) return 0;
  // Custom collar override — user sets weight directly on the Builder slider.
  if (collar.isCustom) {
    return state.customCollarWeight ?? collar.weight;
  }
  return collar.weight;
}

export function getNockWeight(state: BuildState): number {
  return NOCKS[state.nockIdx]?.weight ?? 0;
}

export function getWrapWeight(state: BuildState): number {
  return state.wrap ? state.wrapLen * WRAP_GR_PER_INCH : 0;
}

export function getPointCategory(state: BuildState): string {
  return POINTS[state.pointPreset]?.category ?? 'field';
}

/** Outside diameter in inches (parsed from shaft.od). */
export function getShaftDiameterInches(state: BuildState): number {
  const raw = getShaft(state).od.replace(/[^0-9.]/g, '');
  const v = parseFloat(raw);
  return Number.isFinite(v) ? v : 0.234;
}

// ============================================================
// Component contributions — refined positions
// ============================================================

export function getComponents(state: BuildState): ComponentContribution[] {
  const L = state.length;
  const vane = VANES[state.vaneIdx];
  const vaneLen = vane ? parseVaneLength(vane.length) : DEFAULT_VANE_LENGTH;
  const vaneBaseOffset = getVaneBaseOffset(state);
  const vaneCentroid = vaneBaseOffset + vaneLen / 2;

  const list: ComponentContribution[] = [
    { label: 'Nock', weight: getNockWeight(state), position: POS_NOCK, color: '#71717a' },
    {
      label: `Vanes (×${state.vaneCount})`,
      weight: getVaneWeight(state) * state.vaneCount,
      position: vaneCentroid,
      color: '#fb923c',
    },
  ];

  if (state.wrap) {
    list.push({
      label: 'Wrap',
      weight: getWrapWeight(state),
      position: WRAP_BASE_OFFSET + state.wrapLen / 2,
      color: '#a78bfa',
    });
  }

  list.push(
    { label: 'Shaft', weight: getSpineGPI(state) * L, position: L / 2, color: '#52525b' },
    {
      label: 'Insert',
      weight: getInsertWeight(state),
      position: L - INSERT_OFFSET_FROM_TIP,
      color: '#94a3b8',
    },
  );

  // Impact collar — optional, sits at the OD just behind the insert/point.
  const collarWeight = getCollarWeight(state);
  if (collarWeight > 0) {
    list.push({
      label: 'Collar',
      weight: collarWeight,
      position: L - INSERT_OFFSET_FROM_TIP + 0.2, // slightly forward of insert centroid
      color: '#cbd5e1',
    });
  }

  list.push(
    {
      label: 'Point',
      weight: state.pointWeight,
      position: L + (POINT_CENTROID_OFFSET[getPointCategory(state)] ?? 0.55),
      color: '#f59e0b',
    },
  );

  // ────────────────────────────────────────────────────────
  // Firenock CTI (Carbon Tube Inserts) — internal reinforcement
  // ────────────────────────────────────────────────────────
  // Front CTI sits in the front of the shaft, behind the insert.
  // Centroid = L - insert_offset - front_length/2.
  //
  // Back CTI sits in the rear of the shaft, but NOT flush against the nock —
  // the first ~2" of the rear shaft volume is occupied by the lighted-nock
  // battery + circuit. The CTI tube starts CTI_REAR_OFFSET_FROM_SHAFT_BACK
  // forward of the rear cut. So measuring from nock throat:
  //   back_start = NOCK_THROAT_TO_SHAFT_BACK + CTI_REAR_OFFSET_FROM_SHAFT_BACK
  //   centroid   = back_start + back_length/2
  if (state.ctiPosition === 'front' || state.ctiPosition === 'both') {
    const frontLen = state.ctiFrontLength;
    const frontCentroid = L - INSERT_OFFSET_FROM_TIP - frontLen / 2;
    list.push({
      label: `CTI Front (${frontLen}")`,
      weight: state.ctiFrontWeight,
      position: frontCentroid,
      color: '#0ea5e9', // sky-500
    });
  }
  if (state.ctiPosition === 'both') {
    const backLen = state.ctiBackLength;
    const backStart = NOCK_THROAT_TO_SHAFT_BACK + CTI_REAR_OFFSET_FROM_SHAFT_BACK;
    const backCentroid = backStart + backLen / 2;
    list.push({
      label: `CTI Back (${backLen}")`,
      weight: state.ctiBackWeight,
      position: backCentroid,
      color: '#0284c7', // sky-600
    });
  }

  return list;
}

// ============================================================
// Core calculations
// ============================================================

export function totalWeight(state: BuildState): number {
  return getComponents(state).reduce((s, c) => s + c.weight, 0);
}

/**
 * The effective launch velocity (fps) for KE / momentum / trajectory: the
 * user's measured chronograph reading when they've entered one, otherwise the
 * IBO-based estimate for the current bow + arrow weight.
 *
 * This is the single source of truth for `state.fps`. Resolving it on every
 * build edit (in the reducer) fixes the long-standing bug where `fps` sat at a
 * static 285 in every UI mode except Expert — so KE was computed at 285 fps
 * regardless of the bow. Now KE tracks the bow by default, and a single
 * measured chrono reading overrides it.
 */
export function resolveLaunchFps(state: BuildState): number {
  if (state.measuredFps && state.measuredFps > 0) return Math.round(state.measuredFps);
  return Math.round(estimateVelocity({ bow: state.bow, arrowWeightGrains: totalWeight(state) }));
}

/**
 * Balance point in inches from nock throat — Σ(W·x) / Σ(W).
 *
 * Source: Newton's first moment of area applied to mass; standard CG formula
 * (Beer & Johnston, "Vector Mechanics for Engineers", §5.1).
 */
export function balancePoint(state: BuildState): number {
  const components = getComponents(state);
  const totalMoment = components.reduce((s, c) => s + c.weight * c.position, 0);
  const totalW = components.reduce((s, c) => s + c.weight, 0);
  return totalW > 0 ? totalMoment / totalW : 0;
}

/**
 * FOC % per Easton / ATA standard:
 *   FOC = ((A − L/2) / L) × 100
 *   A = nock throat → balance point (inches)
 *   L = nock throat → end of shaft (inches) — point NOT included
 *
 * Source: Easton Archery Technical Bulletin, "Determining the FOC of Hunting
 * Arrows" (Easton.com, 2013).
 */
export function foc(state: BuildState): number {
  const L = state.length;
  if (L <= 0) return 0;
  return ((balancePoint(state) - L / 2) / L) * 100;
}

/**
 * Kinetic Energy in ft·lb.
 *   KE = ½ m v²
 *   In imperial archery units: KE = (W_gr × V_fps²) / 450,240
 *
 * Source: Easton Technical Bulletin "Kinetic Energy and Momentum".
 */
export function ke(state: BuildState): number {
  const w = totalWeight(state);
  return (w * state.fps * state.fps) / KE_DIVISOR;
}

/**
 * Momentum in slug·ft/s.
 *   p = m × v
 *   In imperial archery units: p = (W_gr × V_fps) / 225,400
 *
 * Source: derived; matches Ashby's "Arrow Penetration" Study 1 momentum tables.
 */
export function momentum(state: BuildState): number {
  const w = totalWeight(state);
  return (w * state.fps) / MOMENTUM_DIVISOR;
}

/** All derived values in a single pass — preferred for components that need multiple. */
export function deriveAll(state: BuildState): BuildDerived {
  const w = totalWeight(state);
  const bp = balancePoint(state);
  /* Guard divide-by-length (matches foc()): a zero/blank length must not
   * yield Infinity/NaN, which would poison the recommender's score sort. */
  const focVal = state.length > 0 ? ((bp - state.length / 2) / state.length) * 100 : 0;
  const od = getShaftDiameterInches(state);
  const vane = VANES[state.vaneIdx];
  const vaneLen = vane ? parseVaneLength(vane.length) : DEFAULT_VANE_LENGTH;
  const category = getPointCategory(state);

  return {
    weight: w,
    balancePoint: bp,
    foc: focVal,
    ke: (w * state.fps * state.fps) / KE_DIVISOR,
    momentum: (w * state.fps) / MOMENTUM_DIVISOR,
    bc: ballisticCoefficient({
      weightGrains: w,
      diameterInches: od,
      pointCategory: category,
    }),
    centerOfPressure: centerOfPressure({
      vaneBaseOffset: getVaneBaseOffset(state),
      vaneLength: vaneLen,
      vaneCount: state.vaneCount,
      arrowLength: state.length,
    }),
    stabilityCalibers: 0, // populated below
    estimatedFps: estimateVelocity({
      bow: state.bow,
      arrowWeightGrains: w,
    }),
  };
}

// Wrapper that finishes stabilityCalibers (requires both CG and CP).
export function deriveAllWithStability(state: BuildState): BuildDerived {
  const d = deriveAll(state);
  const od = getShaftDiameterInches(state);
  d.stabilityCalibers = od > 0 ? (d.balancePoint - d.centerOfPressure) / od : 0;
  return d;
}

// ============================================================
// FOC zone classification
// ============================================================

/**
 * FOC zones — Sparrow hybrid model (v115, June 2026).
 *
 * Anchored on Yates 2026 Arrow Ballistics Study (modern compounds, ~1000
 * arrows, Easton + PCA + Hoyt) more than Ashby's 1980s–90s penetration
 * studies, because modern 70-lb compounds at 280–310 fps deliver enough
 * kinetic energy that penetration stops being the binding constraint for
 * most North American big game. Yates' regression: broadhead group radius
 * monotonically tightens with FOC, bounded by dynamic spine + trajectory —
 * no specific sweet spot in his data. We synthesize: 11–15% captures most
 * of the modern accuracy benefit while keeping trajectory hunter-friendly
 * and spine easy to tune. Higher FOC is genuinely better with explicit
 * tradeoffs (trajectory loss, spine sensitivity).
 *
 * Tier philosophy:
 *   - 11–15% is RECOMMENDED, not "conservative." A 12% build is not
 *     under-tuned — it's exactly where most modern compound hunters should be.
 *   - 15–19% is a "high" tier we present even-handedly: real pros, real cons.
 *   - 19%+ is specialized (dangerous game, traditional, Ashby disciples).
 *   - Below 8% is the only band we flag as a problem.
 */
export const FOC_ZONES: readonly FOCZone[] = [
  { min: -Infinity, max: 8, color: 'rose',
    label: 'Low FOC — accuracy & wind issues.',
    detail: 'Below 8% the arrow tends to steer poorly in crosswind and groups widen. Three highest-leverage fixes (Yates 2026): cut the shaft shorter and move the weight forward, drop to a lower-GPI shaft and reallocate to the point, or use lighter vanes that still steer your broadhead.' },
  { min: 8, max: 11, color: 'amber',
    label: 'Standard — target / 3D / light game.',
    detail: 'A common modern setup. Perfect for target shooting, 3D, indoor leagues, small-game hunting. If you plan to hunt big game with this build, consider pushing FOC to 11%+ for tighter broadhead groups at distance.' },
  { min: 11, max: 15, color: 'emerald',
    label: 'Recommended hunter range.',
    detail: 'The practical sweet spot for most North American compound hunters. Modern 70-lb bows at 280+ fps deliver more than enough kinetic energy for whitetail / black bear / antelope here, and broadhead accuracy is excellent. NOT under-tuned — emphatically the right zone for most builds.' },
  { min: 15, max: 19, color: 'emerald',
    label: 'High FOC — big-game upside.',
    detail: 'Pros: ~2" tighter broadhead group at 70 yd per +5 pts FOC (Yates 2026), real penetration upside for elk / moose / large bear, more forgiving of slight tuning errors. Cons: trajectory drop becomes noticeable past 50 yd, requires stiffer dynamic spine, can amplify release torque. Worth it if you hunt big animals; overkill for whitetail.' },
  { min: 19, max: Infinity, color: 'violet',
    label: 'Extreme FOC — specialized.',
    detail: 'Dangerous game, traditional setups, Ashby-school archers. Penetration gains are real but trajectory cost is significant — practice 40+ yd shots so you internalize the drop. Dynamic spine tuning gets harder; you usually need a noticeably stiffer shaft than your bow chart suggests.' },
];

export function classifyFOC(focPercent: number): FOCZone {
  return FOC_ZONES.find((z) => focPercent >= z.min && focPercent < z.max)
    ?? FOC_ZONES[FOC_ZONES.length - 1];
}

// ============================================================
// KE game classification
// ============================================================

export interface KEClassification {
  label: string;
  level: 'insufficient' | 'small' | 'large' | 'heavy';
}

/**
 * Game-class KE thresholds.
 * Source: Easton Archery Hunting Reference Card (legal-minimum side) +
 * Whitetail Stewards "Bowhunting Ethics" KE charts (recommended side).
 */
export function classifyKE(keFtLb: number): KEClassification {
  if (keFtLb < 25) return { label: 'Below most state minimums for game', level: 'insufficient' };
  if (keFtLb < 42) return { label: 'Suitable for deer / small game', level: 'small' };
  if (keFtLb < 65) return { label: 'Suitable for elk & large game', level: 'large' };
  return { label: 'Heavy game / dangerous animal range', level: 'heavy' };
}

// ============================================================
// FOC marker positioning (for the visual bar)
// ============================================================

export function focBarPosition(focPercent: number): number {
  const FOC_MIN = -3;
  const FOC_MAX = 25;
  const pct = ((focPercent - FOC_MIN) / (FOC_MAX - FOC_MIN)) * 100;
  return Math.max(0, Math.min(100, pct));
}
